import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../auth/useAuthStore'
import { getQuote } from '../../api/trading'
import { useSetting } from '../../hooks/useSetting'
import { useIsMarketOpen } from '../../lib/marketHours'
import { useOpeningCutoff } from '../../hooks/useOpeningCutoff'
import { useDailyCloseLimit } from '../../hooks/useDailyCloseLimit'
import { useMaxLossLimit } from '../../hooks/useMaxLossLimit'
import { useMaxGainLimit } from '../../hooks/useMaxGainLimit'
import { useOrbBlock } from '../../hooks/useOrbBlock'
import { useTrendBlock } from '../../hooks/useTrendBlock'
import { ORB_BLOCK_KEY } from '../../lib/orbRule'
import { TREND_BLOCK_KEY } from '../../lib/trendRule'

interface Props {
  symbol: string
}

interface Rule {
  id: string
  label: string
  ok: boolean
  detail: string
}

function num(v: string | undefined | null): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Top-right overlay listing every enabled trading rule. Green = rule allows
 * BUYs right now; red = rule is currently blocking. RTH (market hours) is
 * always shown since it's an implicit baseline; every other rule appears
 * only when configured/enabled in Settings.
 */
export function ChartRulesDashboard({ symbol }: Props) {
  const accountId = useAuthStore((s) => s.accountId)
  const marketOpen = useIsMarketOpen()
  const cutoff = useOpeningCutoff()
  const closeLimit = useDailyCloseLimit()
  const maxLoss = useMaxLossLimit()
  const maxGain = useMaxGainLimit()
  const [orbEnabled] = useSetting<boolean>(ORB_BLOCK_KEY, false)
  const [trendEnabled] = useSetting<boolean>(TREND_BLOCK_KEY, false)

  // Shares the QuoteCard's react-query key so we don't double-fetch.
  const quoteQ = useQuery({
    queryKey: ['quote', symbol, accountId],
    queryFn: () => getQuote(symbol),
    enabled: !!accountId && !!symbol && orbEnabled,
    refetchInterval: marketOpen && orbEnabled ? 5_000 : false,
    refetchIntervalInBackground: false,
  })
  const last = num(quoteQ.data?.quotes?.[0]?.last)

  const orb = useOrbBlock(symbol, last)
  const trend = useTrendBlock(symbol)

  const rules: Rule[] = []

  if (cutoff.time) {
    rules.push({
      id: 'cutoff',
      label: `Cutoff ${cutoff.time}`,
      ok: !cutoff.active,
      detail: cutoff.reason ?? `Before cutoff (${cutoff.time} ET)`,
    })
  }

  if (closeLimit.cap != null) {
    rules.push({
      id: 'closecap',
      label: `Closes ${closeLimit.todayClosedCount}/${closeLimit.cap}`,
      ok: !closeLimit.capReached,
      detail:
        closeLimit.capReason ??
        `${closeLimit.todayClosedCount} of ${closeLimit.cap} closed positions today`,
    })
  }

  // Single P/L pill — flips between "Gain X/Y" and "Loss X/Y" based on the
  // sign of today's realized P/L. Prefer the side that matches the day's
  // direction; fall back to whichever side is configured.
  {
    const dailyPL = maxGain.dailyGain // identical signed value across both hooks
    const wantGain = dailyPL >= 0
    const gainConfigured = maxGain.type != null && maxGain.value != null
    const lossConfigured = maxLoss.type != null && maxLoss.value != null
    const showGain = gainConfigured && (wantGain || !lossConfigured)
    const showLoss = lossConfigured && (!wantGain || !gainConfigured)

    if (showGain) {
      const label =
        maxGain.type === 'percent'
          ? `Gain ${
              maxGain.dailyPct != null
                ? `${Math.max(0, maxGain.dailyPct).toFixed(2)}%`
                : '—'
            }/${maxGain.value}%`
          : `Gain $${Math.max(0, maxGain.dailyGain).toFixed(0)}/$${maxGain.value}`
      const fallback =
        maxGain.type === 'percent'
          ? `Daily gain within limit (${maxGain.value}% cap)`
          : `Daily gain within limit ($${maxGain.value} cap)`
      rules.push({
        id: 'maxpl',
        label,
        ok: !maxGain.capReached,
        detail: maxGain.capReason ?? fallback,
      })
    } else if (showLoss) {
      const label =
        maxLoss.type === 'percent'
          ? `Loss ${
              maxLoss.dailyPct != null
                ? `${Math.max(0, -maxLoss.dailyPct).toFixed(2)}%`
                : '—'
            }/${maxLoss.value}%`
          : `Loss $${Math.abs(Math.min(0, maxLoss.dailyLoss)).toFixed(0)}/$${maxLoss.value}`
      const fallback =
        maxLoss.type === 'percent'
          ? `Daily loss within limit (${maxLoss.value}% cap)`
          : `Daily loss within limit ($${maxLoss.value} cap)`
      rules.push({
        id: 'maxpl',
        label,
        ok: !maxLoss.capReached,
        detail: maxLoss.capReason ?? fallback,
      })
    }
  }

  if (orbEnabled) {
    rules.push({
      id: 'orb',
      label: 'ORB',
      ok: !orb.active,
      detail: orb.reason ?? 'Outside ORB — opens allowed',
    })
  }

  if (trendEnabled) {
    const arrow = trend.trend === 'up' ? ' ↑' : trend.trend === 'down' ? ' ↓' : ''
    rules.push({
      id: 'trend',
      label: `Trend${arrow}`,
      ok: trend.longReason == null,
      detail:
        trend.longReason ??
        (trend.trend === 'up'
          ? 'With trend (intraday uptrend)'
          : trend.trend === 'down'
            ? 'Counter-trend (intraday downtrend)'
            : 'Sideways / insufficient data'),
    })
  }

  if (rules.length === 0) return null

  return (
    <div className="absolute top-2 right-16 z-20 flex flex-col items-end gap-1">
      {rules.map((r) => (
        <div
          key={r.id}
          title={r.detail}
          className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded border font-mono text-[10px] leading-none bg-zinc-950/75 backdrop-blur-sm whitespace-nowrap ${
            r.ok
              ? 'border-emerald-500/30 text-emerald-300'
              : 'border-red-500/40 text-red-300'
          }`}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              r.ok ? 'bg-emerald-400' : 'bg-red-400'
            }`}
          />
          {r.label}
        </div>
      ))}
    </div>
  )
}
